import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
} from '@tanstack/react-router';

import { Toaster } from '@/components/ui/sonner';

import { RedirectIfAuthenticated } from './components/RedirectIfAuthenticated';
import { RequireAdmin } from './components/RequireAdmin';
import { RequireAuth } from './components/RequireAuth';
import {
  RequireAuthWithUpgrade,
  RequireSubscription,
} from './components/RequireSubscription';
import { queryClient } from './lib/queryClient';
import {
  AccountPage,
  AdminDashboardPage,
  ArticleDetails,
  AuthPage,
  ContactPage,
  CreateAlertPage,
  GraphsPage,
  Home,
  NotFound,
  PrivacyPolicyPage,
  PromptRunsPage,
  ReportsPage,
  TermsPage,
  UpgradePage,
} from './pages';

function requireAuth(page: React.ReactNode) {
  return () => <RequireAuth>{page}</RequireAuth>;
}

function requireAdmin(page: React.ReactNode) {
  return () => (
    <RequireAuth>
      <RequireAdmin>{page}</RequireAdmin>
    </RequireAuth>
  );
}

const rootRoute = createRootRoute({
  component: () => (
    <>
      <Outlet />
      <Toaster />
    </>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: () => <Home />,
});

const articleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/article/$id',
  component: () => <ArticleDetails />,
});

const graphsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/graphs',
  component: () => (
    <RequireAuthWithUpgrade>
      <GraphsPage />
    </RequireAuthWithUpgrade>
  ),
});

const upgradeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/upgrade',
  component: () => <UpgradePage />,
});

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  component: requireAdmin(<AdminDashboardPage />),
});

const createAlertRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/create-alert',
  component: () => (
    <RequireSubscription feature="alerts">
      <CreateAlertPage />
    </RequireSubscription>
  ),
  loader: () =>
    queryClient.fetchQuery({
      queryKey: ['watchers'],
      queryFn: async () => {
        const res = await fetch('/api/watchers');
        if (!res.ok) {
          return [];
        }
        const data = await res.json();
        return data.watchers || [];
      },
    }),
});

const promptRunsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/prompt-runs',
  component: () => (
    <RequireSubscription feature="alerts">
      <PromptRunsPage />
    </RequireSubscription>
  ),
});

const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/$authFlow',
  component: () => (
    <RedirectIfAuthenticated>
      <AuthPage />
    </RedirectIfAuthenticated>
  ),
});

const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account/$accountFlow',
  component: requireAuth(<AccountPage />),
});

const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/terms',
  component: TermsPage,
});

const privacyPolicyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy-policy',
  component: PrivacyPolicyPage,
});

const reportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports',
  component: ReportsPage,
});

const contactRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/contact',
  component: ContactPage,
});

const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '*',
  component: NotFound,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  articleRoute,
  graphsRoute,
  reportsRoute,
  upgradeRoute,
  createAlertRoute,
  adminRoute,
  promptRunsRoute,
  authRoute,
  accountRoute,
  termsRoute,
  privacyPolicyRoute,
  contactRoute,
  notFoundRoute,
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
